<script setup lang="ts">
import Card from "primevue/card";
import { computed } from "vue";
import { useRouter } from "vue-router";
import useExperiences from "@/components/MyExperiences/composables/useExperiences.js";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import { ORDER_STATUS } from "@/constants";
import { endOfDayDate } from "@/util/date";

const props = defineProps({
  experience: {
    type: Object,
    required: true,
  },
  panelDefinition: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["start-loading", "image-loaded"]);

const router = useRouter();

const { programStartMessage, showStartMessage, formatPrettyDate } = useExperiences();

const { secondaryColorClassButton, secondaryColorClassOutlined } = useTenantTheming();

const buttonLabel = computed(() => {
  if (props.experience.order_status === ORDER_STATUS.draft) {
    return "Incomplete";
  } else if (props.experience.order_status === ORDER_STATUS.withdrawn) {
    return "Withdrawn";
  }

  if (props.panelDefinition.buttonText) {
    return props.panelDefinition.buttonText;
  }
  return props.experience.order_status;
});

const imageUrl = computed(() => {
  if (props.experience?.image_urls.length === 0) {
    return "";
  }
  return props.experience.image_urls[0];
});

const isCardDisabled = computed(
  () =>
    !showStartMessage(endOfDayDate(props.experience?.application_deadline)) &&
    props.experience.order_status === ORDER_STATUS.draft,
);

const buttonLink = async (applicationId) => {
  if (props.panelDefinition.buttonRouteName) {
    emit("start-loading");
    // Navigate in-SPA rather than a full-page reload. The full reload froze the page's
    // `cardActionLoading` state into the browser bfcache, so pressing Back from the
    // destination restored My Experiences stuck on its loading spinner (AP-5086 #2c).
    await router.push({
      name: props.panelDefinition.buttonRouteName,
      params: {
        applicationId: applicationId,
      },
    });
  }
};

const deadlineText = computed(() => {
  const title = props.experience.session_require_application ? "Application Deadline" : "Registration Deadline";
  const date = formatPrettyDate(props.experience.application_deadline);
  return `${title}: ${date}`;
});

const ptStyling = {
  body: {
    class: "flex flex-col flex-grow gap-2 p-4",
  },
  content: {
    class: " font-normal text-sm leading-[163%] text-[#334155]",
  },
  caption: {
    class: "flex flex-col flex-grow gap-2",
  },
  title: {
    class: "flex flex-grow font-semibold text-lg text-[#334155]",
  },
  subtitle: {
    class: "font-normal text-sm text-[#647488]",
  },
  footer: {
    class: "pt-0",
  },
};

const customStyling = computed(() => {
  let classStyle = "";
  if (isCardDisabled.value) {
    classStyle += " opacity-50";
  }
  return classStyle;
});

const buttonStyling = (historical: boolean) => {
  const layout = "text-sm font-semibold rounded-md px-3 py-2 w-max border transition-colors";
  if (historical) {
    return `${layout} bg-white ${secondaryColorClassOutlined.value}`;
  }
  return `${layout} ${secondaryColorClassButton.value}`;
};

const handleImageLoad = () => {
  emit("image-loaded");
};
</script>
<template>
  <div class="flex flex-col flex-grow w-full" :class="customStyling">
    <Card
      class="flex flex-col flex-grow rounded-2x1 !shadow-md bg-white gap-2 overflow-hidden"
      :pt="ptStyling"
      :data-testid="`my-experiences-summary-card-${experience.application_id}`"
    >
      <template #header>
        <img
          alt="Experience Image"
          :src="imageUrl"
          class="w-full h-48 object-cover rounded-t-lg"
          :data-testid="`my-experiences-summary-card-${experience.application_id}-img`"
          @load="handleImageLoad"
          @error="handleImageLoad"
        />
      </template>
      <template #title>{{ experience.session_name }}</template>
      <template #subtitle>{{ experience.city_name }}, {{ experience.country_name }}</template>
      <template #content>
        <p>{{ formatPrettyDate(experience.start_date) }} – {{ formatPrettyDate(experience.end_date) }}</p>
        <p v-if="showStartMessage(experience.start_date)">{{ programStartMessage(experience.start_date) }}</p>
        <p>{{ deadlineText }}</p>
      </template>
      <template #footer>
        <div class="flex flex-row-reverse w-full p-0">
          <button
            :class="buttonStyling(panelDefinition.historical)"
            :data-testid="`my-experiences-summary-card-${experience.application_id}-continue-button`"
            @click="buttonLink(experience.application_id)"
            :disabled="isCardDisabled"
          >
            {{ buttonLabel }}
          </button>
        </div>
      </template>
    </Card>
  </div>
</template>
